/**
 * ChannelsTab — gerenciamento de canais de comunicação do agente.
 *
 * Master-detail:
 *   - view='grid' → cards de todos os canais (do registry)
 *   - view='detail' → renderiza detail do canal selecionado
 *   - view='connect' → renderiza connect flow do canal selecionado
 *
 * Integração com alertas:
 *   - AlertBanner mostra alertas persistentes (token_invalid, webhook_silent)
 *   - BlockingAlertModal força reconexão quando token_invalid
 *   - AlertInbox no header
 *   - AlertToast renderizado pelo provider em qualquer lugar
 */

import React, { useState, useCallback } from 'react';
import { ChannelAlertsProvider } from '../../../contexts/ChannelAlertsContext';
import { useAgentsFlow } from '../../../contexts/AgentsFlowContext';
import ChannelsHeader from './ChannelsHeader';
import ChannelsGrid from './ChannelsGrid';
import ChannelDetailWrapper from './ChannelDetailWrapper';
import AlertBanner from '../../alerts/AlertBanner';
import AlertToast from '../../alerts/AlertToast';
import BlockingAlertModal from '../../alerts/BlockingAlertModal';
import PublishGateOverlay from './PublishGateOverlay';
import { getChannelById } from '../../../channels/registry';
import './ChannelsTab.css';

const VIEW = {
  GRID: 'grid',
  DETAIL: 'detail',
  CONNECT: 'connect',
};

const ChannelsTabInner = ({ agentId }) => {
  const [view, setView] = useState(VIEW.GRID);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [filterAlertOnly, setFilterAlertOnly] = useState(false);
  // Sub-section solicitada por CTAs de alerta (ex: 'templates', 'health')
  const [pendingSubSection, setPendingSubSection] = useState(null);

  // Gating de publicação: agentes nunca publicados não podem conectar canais.
  // O context atualiza is_published após o publish bem-sucedido — overlay
  // desmonta automaticamente.
  const { state } = useAgentsFlow();
  const isNewAgent = !agentId || agentId === 'novo';
  // currentAgent ainda não carregado (id existe mas dado não chegou) → evita
  // flash de overlay enquanto state hidrata. Considera carregado só quando
  // o ID bate com o agentId atual.
  const agentLoaded =
    !!state.currentAgent && String(state.currentAgent.id) === String(agentId);
  const isPublished = !!state.currentAgent?.is_published;
  const showPublishGate = !isNewAgent && agentLoaded && !isPublished;

  const selectedChannel = selectedChannelId ? getChannelById(selectedChannelId) : null;

  const handleSelectChannel = useCallback((channelId) => {
    setSelectedChannelId(channelId);
    setView(VIEW.DETAIL);
  }, []);

  const handleConnect = useCallback((channelId) => {
    setSelectedChannelId(channelId);
    setView(VIEW.CONNECT);
  }, []);

  const handleBackToGrid = useCallback(() => {
    setView(VIEW.GRID);
    setPendingSubSection(null);
    // Mantém selectedChannelId para o usuário voltar facilmente se quiser
  }, []);

  const handleConnected = useCallback(() => {
    setView(VIEW.DETAIL); // Após conectar, vai direto para detail
    setPendingSubSection(null);
  }, []);

  // CTAs de alertas — navegação contextual
  const handleAlertCTA = useCallback((action, alert) => {
    // Por enquanto, todos os alertas Cloud API levam para o canal Cloud API.
    // No futuro, derivar canal a partir de alert.channelKey.
    const targetChannelId = 'whatsapp_cloud_api';

    setSelectedChannelId(targetChannelId);

    if (action === 'open_channel_connect') {
      setView(VIEW.CONNECT);
    } else if (action === 'open_channel_templates') {
      setView(VIEW.DETAIL);
      setPendingSubSection('templates');
    } else if (action === 'open_channel_detail') {
      setView(VIEW.DETAIL);
      setPendingSubSection(null);
    } else {
      setView(VIEW.DETAIL);
    }
  }, []);

  const navigation = {
    goBackToGrid: handleBackToGrid,
    switchToChannel: handleSelectChannel,
    switchToConnect: (channelId) => {
      setSelectedChannelId(channelId);
      setView(VIEW.CONNECT);
    },
    pendingSubSection,
    clearPendingSubSection: () => setPendingSubSection(null),
  };

  return (
    <div className={`channels-tab ${showPublishGate ? 'channels-tab--locked' : ''}`}>
      <BlockingAlertModal onCTA={handleAlertCTA} />
      <AlertToast />

      {showPublishGate && <PublishGateOverlay agentId={agentId} />}

      {/* Wrapper "behind overlay" — quando locked, vira preview desabilitado */}
      <div className="channels-tab__content">
      {view === VIEW.GRID && (
        <>
          <ChannelsHeader
            agentId={agentId}
            filterAlertOnly={filterAlertOnly}
            onToggleFilter={() => setFilterAlertOnly((v) => !v)}
            onAlertCTA={handleAlertCTA}
          />
          <AlertBanner onCTA={handleAlertCTA} />
          <ChannelsGrid
            agentId={agentId}
            filterAlertOnly={filterAlertOnly}
            onSelect={handleSelectChannel}
            onConnect={handleConnect}
          />
        </>
      )}

      {view === VIEW.DETAIL && selectedChannel && (
        <ChannelDetailWrapper
          agentId={agentId}
          channel={selectedChannel}
          navigation={navigation}
          onAlertCTA={handleAlertCTA}
        />
      )}

      {view === VIEW.CONNECT && selectedChannel && selectedChannel.renderConnectFlow && (
        <ChannelDetailWrapper
          agentId={agentId}
          channel={selectedChannel}
          navigation={navigation}
          mode="connect"
          onAlertCTA={handleAlertCTA}
          onConnected={handleConnected}
        />
      )}
      </div>
    </div>
  );
};

const ChannelsTab = ({ agentId }) => (
  <ChannelAlertsProvider>
    <ChannelsTabInner agentId={agentId} />
  </ChannelAlertsProvider>
);

export default ChannelsTab;
